/**
 * Period resolution — URL search params → the window every widget reads.
 *
 * One dashboard-level window is the default; a widget may override it with
 * its own preset or custom range. Granularity is DERIVED from the window
 * length rather than stored, with one narrow user override:
 *
 *   ≤ 31 days → day · ≤ 182 days → week · otherwise → month
 *
 * The thresholds exist to keep the bucket count under the BE's 400-bucket
 * refusal while staying legible: 31 daily bars fit a card, 366 do not.
 * The Weekly / Monthly toggle appears ONLY above 31 days — below it the
 * auto choice is already `day`, and offering "group 12 days by month"
 * produces a one-bar chart that answers nothing.
 *
 * Dates here are local calendar dates (`YYYY-MM-DD`) in the viewer's zone,
 * matching the BE contract: the server buckets in the viewer's timezone
 * and the string is the join key on both sides. No `Date` ever crosses
 * this boundary.
 */

import type { DateRange, DateRangePreset, FirstDayOfWeek } from '@/features/time/date-helpers'
import {
  addMonths,
  daysInRange,
  endOfMonth,
  endOfQuarter,
  endOfYear,
  parseIsoDate,
  rangeForPreset,
  shiftRange,
  shiftUnitForPreset,
  startOfMonth,
  startOfQuarter,
  startOfWeek,
  startOfYear,
  toIsoDate,
} from '@/features/time/date-helpers'

import type { ReportGranularity, ReportPeriodOverride, ReportWidgetConfig } from '../types'

export const DEFAULT_PERIOD_PRESET: DateRangePreset = 'this-month'

/** Above this many days the Weekly / Monthly toggle appears. */
export const GRANULARITY_OVERRIDE_MIN_DAYS = 31
/** Auto-granularity crosses from week to month above this many days. */
export const WEEK_GRANULARITY_MAX_DAYS = 182

const ISO_DATE_RE = /^\d{4}-\d{2}-\d{2}$/

export function isIsoDate(value: unknown): value is string {
  return typeof value === 'string' && ISO_DATE_RE.test(value)
}

/** Bucket size for a window of `days` calendar days. */
export function autoGranularity(days: number): ReportGranularity {
  if (days <= GRANULARITY_OVERRIDE_MIN_DAYS) return 'day'
  if (days <= WEEK_GRANULARITY_MAX_DAYS) return 'week'
  return 'month'
}

/** Whether the Weekly / Monthly toggle is offered for this window. */
export function allowsGranularityOverride(days: number): boolean {
  return days > GRANULARITY_OVERRIDE_MIN_DAYS
}

/** The two values the toggle can carry. `day` is never user-selectable. */
export type GranularityOverride = 'week' | 'month'

export interface DashboardWindow extends DateRange {
  /** The preset in force, or `null` when the user picked a custom range. */
  preset: DateRangePreset | null
  granularity: ReportGranularity
  /** The user's explicit toggle choice, when one is in force. */
  granularityOverride: GranularityOverride | null
  /** Inclusive day count — cached because three callers need it. */
  days: number
}

export interface ReportsSearchParams {
  period?: DateRangePreset
  from?: string
  to?: string
  gran?: GranularityOverride
  /**
   * Customize mode. The literal `1`, not `true`: TanStack Router
   * serializes whatever value it holds, so a normalized `true` rewrote a
   * hand-typed (and documented, and e2e-selected) `?edit=1` into
   * `?edit=true` in the address bar. Reads accept `1 | '1' | true |
   * 'true'`; only `1` is ever written.
   */
  edit?: 1
  /**
   * DEV-ONLY. Renders the chart kit's fixture gallery instead of the
   * dashboard, so every chart × size × dataset (empty, one series,
   * 8+Others, 365 days, long names, single-slice donut, null gaps) can
   * be swept in one page across the three themes. The route validator
   * DROPS it in a production build, so the param cannot reach a
   * customer's URL bar even by hand.
   */
  fixture?: true
}

export interface ResolveWindowOptions {
  firstDay?: FirstDayOfWeek
  /** Injectable "now" so the resolution tests don't depend on the clock. */
  today?: Date
}

/**
 * Turn the URL into the dashboard window.
 *
 * A CUSTOM range wins over a preset whenever both `from` and `to` parse
 * and are ordered — the period picker writes one or the other, never
 * both, but a hand-edited URL can carry both and the explicit dates are
 * the more specific instruction. An inverted or malformed pair is
 * IGNORED rather than surfaced as an error: the BE would refuse it with
 * `reports_invalid_range` and there is nothing useful to render behind
 * that, so the dashboard falls back to the preset (or the default) and
 * stays usable.
 */
export function resolveDashboardWindow(
  search: ReportsSearchParams,
  options: ResolveWindowOptions = {},
): DashboardWindow {
  const custom =
    isIsoDate(search.from) && isIsoDate(search.to) && search.from <= search.to
      ? { from: search.from, to: search.to }
      : null

  const preset = custom ? null : (search.period ?? DEFAULT_PERIOD_PRESET)
  const range = custom ?? rangeForPreset(preset ?? DEFAULT_PERIOD_PRESET, options)
  const days = daysInRange(range)

  const override =
    search.gran && allowsGranularityOverride(days) ? (search.gran as GranularityOverride) : null

  return {
    ...range,
    preset,
    days,
    granularity: override ?? autoGranularity(days),
    granularityOverride: override,
  }
}

export interface EffectiveWindow extends DateRange {
  granularity: ReportGranularity
  days: number
  /** True when this window came from the widget's own period override. */
  isOverride: boolean
}

/**
 * The window one widget actually plots.
 *
 * A widget override carries no granularity of its own in v1 — the auto
 * rule applies to whatever range it resolves to, so a widget pinned to
 * "Last 12 months" inside a dashboard showing "This week" still buckets
 * by month rather than inheriting the dashboard's daily choice.
 */
export function effectiveWindowFor(
  config: ReportWidgetConfig | undefined,
  dashboard: DashboardWindow,
  options: ResolveWindowOptions = {},
): EffectiveWindow {
  const range = resolveOverrideRange(config?.periodOverride, options)
  if (!range) {
    return {
      from: dashboard.from,
      to: dashboard.to,
      granularity: dashboard.granularity,
      days: dashboard.days,
      isOverride: false,
    }
  }
  const days = daysInRange(range)
  return { ...range, days, granularity: autoGranularity(days), isOverride: true }
}

/** `null` ⇒ follow the dashboard. Unparseable overrides also follow it. */
export function resolveOverrideRange(
  override: ReportPeriodOverride | undefined,
  options: ResolveWindowOptions = {},
): DateRange | null {
  if (!override) return null
  if ('preset' in override) return rangeForPreset(override.preset, options)
  if (isIsoDate(override.from) && isIsoDate(override.to) && override.from <= override.to) {
    return { from: override.from, to: override.to }
  }
  return null
}

/**
 * Step the dashboard window by one unit.
 *
 * A preset window steps by ITS unit (a month preset moves a whole month,
 * not 30 days); a custom range steps by its own length. Stepping always
 * produces an explicit `from`/`to` pair rather than a neighbouring preset
 * slug — "the month before this month" has no preset name, and inventing
 * one ("last-month") would be wrong the moment the user steps twice.
 */
export function shiftDashboardWindow(window: DashboardWindow, delta: number): DateRange {
  const unit = window.preset ? shiftUnitForPreset(window.preset) : 'days'
  return shiftRange({ from: window.from, to: window.to }, delta, unit)
}

/**
 * Whether the `›` arrow is live. Stepping forward is refused once the
 * window would START in the future — a period nobody has worked in yet
 * charts as an empty grid, and the arrow silently producing one reads as
 * a broken control rather than as an answer.
 */
export function canStepForward(window: DashboardWindow, today = new Date()): boolean {
  const next = shiftDashboardWindow(window, 1)
  const todayIso = toIsoDate(today)
  return next.from <= todayIso
}

/* -------------------------------------------------------------------------- */
/*                             Range description                              */
/* -------------------------------------------------------------------------- */

export interface DescribeRangeDeps {
  /** BCP-47 tag — thread `i18n.language`, never read the system locale. */
  locale: string
  /** `t('period.quarterLabel', { quarter, year })`. */
  quarterLabel: (quarter: number, year: number) => string
}

/**
 * A human label for a window, recognising the shapes that have a NAME.
 *
 * "September 2026" is worth strictly more than "Sep 1 – Sep 30, 2026" —
 * it is shorter, and it tells the reader the window is a whole month
 * rather than something that happens to start and end near one. The
 * fallthrough is the explicit pair, which is the honest answer for a
 * hand-picked range.
 *
 * Every format here goes through `Intl` with the passed locale. Bare
 * `en` is pinned to `en-US` for the same reason `formatters.ts` pins its
 * `longDate`: platforms disagree on what unqualified English means, and
 * the month-day ordering flips between them.
 */
export function describeRange(range: DateRange, deps: DescribeRangeDeps): string {
  const locale = deps.locale === 'en' ? 'en-US' : deps.locale
  const from = parseIsoDate(range.from)
  const to = parseIsoDate(range.to)

  const sameYear = from.getFullYear() === to.getFullYear()

  // Whole calendar year.
  if (
    sameYear &&
    range.from === toIsoDate(startOfYear(from)) &&
    range.to === toIsoDate(endOfYear(to))
  ) {
    return new Intl.DateTimeFormat(locale, { year: 'numeric' }).format(from)
  }

  // Whole calendar quarter.
  if (
    sameYear &&
    range.from === toIsoDate(startOfQuarter(from)) &&
    range.to === toIsoDate(endOfQuarter(from))
  ) {
    return deps.quarterLabel(Math.floor(from.getMonth() / 3) + 1, from.getFullYear())
  }

  // Whole calendar month.
  if (range.from === toIsoDate(startOfMonth(from)) && range.to === toIsoDate(endOfMonth(from))) {
    return new Intl.DateTimeFormat(locale, { month: 'long', year: 'numeric' }).format(from)
  }

  // A run of whole months (the 12-month preset and its shifted siblings).
  if (
    range.from === toIsoDate(startOfMonth(from)) &&
    range.to === toIsoDate(endOfMonth(to)) &&
    range.from !== range.to
  ) {
    const monthYear = new Intl.DateTimeFormat(locale, { month: 'short', year: 'numeric' })
    return `${monthYear.format(from)} – ${monthYear.format(to)}`
  }

  // Single day.
  if (range.from === range.to) {
    return new Intl.DateTimeFormat(locale, { dateStyle: 'medium' }).format(from)
  }

  const dayMonth = new Intl.DateTimeFormat(locale, { month: 'short', day: 'numeric' })
  const dayMonthYear = new Intl.DateTimeFormat(locale, {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  })
  // The year is printed ONCE at the end when both ends share it, and on
  // both ends when they don't — an end-only year on a window spanning
  // New Year would attach 2027 to a December date.
  return sameYear
    ? `${dayMonth.format(from)} – ${dayMonthYear.format(to)}`
    : `${dayMonthYear.format(from)} – ${dayMonthYear.format(to)}`
}

/**
 * Whether a range is exactly one week anchored on the viewer's first day.
 * Not used for the label (a named week reads worse than its dates) but the
 * period picker highlights the matching preset with it.
 */
export function isWholeWeek(range: DateRange, firstDay: FirstDayOfWeek): boolean {
  if (daysInRange(range) !== 7) return false
  const from = parseIsoDate(range.from)
  return toIsoDate(startOfWeek(from, firstDay)) === range.from
}

/** Which preset (if any) a bare `{from,to}` pair happens to equal. */
export function matchPreset(
  range: DateRange,
  presets: readonly DateRangePreset[],
  options: ResolveWindowOptions = {},
): DateRangePreset | null {
  for (const preset of presets) {
    const candidate = rangeForPreset(preset, options)
    if (candidate.from === range.from && candidate.to === range.to) return preset
  }
  return null
}

/**
 * The BE refuses a span over 731 days outright. The picker clamps the
 * custom-range inputs to it so the refusal is prevented rather than
 * translated.
 */
export const MAX_WINDOW_DAYS = 731

/** Latest `to` the custom picker will accept for a given `from`. */
export function maxToForFrom(from: string): string {
  const start = parseIsoDate(from)
  const capped = addMonths(start, 24)
  return toIsoDate(capped)
}
